"use client";

import Link from "next/link";
import { Shield, DollarSign, Stethoscope, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnimatedSection } from "@/hooks/useScrollAnimation";
import { useTranslation } from "react-i18next";

const Features = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Stethoscope,
      title: t("features.feature1Title"),
      description: t("features.feature1Desc"),
    },
    {
      icon: Shield,
      title: t("features.feature2Title"),
      description: t("features.feature2Desc"),
    },
    {
      icon: DollarSign,
      title: t("features.feature3Title"),
      description: t("features.feature3Desc"),
    },
    {
      icon: Users,
      title: t("features.feature4Title"),
      description: t("features.feature4Desc"),
    },
  ];
  return (
    <section className="bg-[#f5f8fc] py-24 px-6 lg:px-12" id="about">
      <div className="max-w-[1200px] mx-auto">
        <AnimatedSection>
          {/* Dark gradient container */}
          <div
            className="relative rounded-[24px] overflow-hidden p-8 lg:p-14"
            style={{
              background: "linear-gradient(135deg, #0a1628 0%, #0f2347 45%, #162d5a 100%)",
              boxShadow: "0 0 0 1px rgba(255,255,255,0.06), 0 32px 80px rgba(0,0,0,0.35)",
            }}
          >
            {/* Subtle inner glow overlay */}
            <div
              className="pointer-events-none absolute inset-0 opacity-30"
              style={{
                background: "radial-gradient(ellipse 70% 60% at 70% 50%, rgba(59,130,246,0.18), transparent 70%)",
              }}
            />
            {/* Noise texture vignette */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                backgroundSize: "256px 256px",
              }}
            />

            {/* 2-column layout */}
            <div className="relative z-10 flex flex-col lg:flex-row gap-10 lg:gap-14 items-stretch">

              {/* LEFT: Video — vertically centered next to the content column */}
              <div className="w-full lg:w-[46%] flex-shrink-0 flex items-center">
                <div
                  className="rounded-[16px] overflow-hidden w-full relative shadow-2xl"
                  style={{
                    boxShadow: "0 0 0 1px rgba(255,255,255,0.08), 0 20px 60px rgba(0,0,0,0.4)",
                    aspectRatio: "16 / 9",
                  }}
                >
                  <iframe
                    src="https://player.vimeo.com/video/1173591185?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    title="Simple. Straightforward. Affordable."
                  />
                </div>
              </div>

              {/* RIGHT: Content */}
              <div className="flex-1 min-w-0 flex flex-col justify-between">

                {/* Main heading */}
                <h2 className="font-heading font-bold text-2xl lg:text-[32px] text-white leading-snug mb-4">
                  {t("features.heading")} <br className="hidden lg:block" />
                  {t("features.headingLine2")}
                </h2>

                {/* Supporting paragraph */}
                <p className="font-body text-sm text-white/75 leading-relaxed mb-8 max-w-[500px]">
                  {t("features.subtitle")}
                </p>

                {/* 4-feature grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                  {features.map((feature, i) => (
                    <div
                      key={feature.title}
                      className="flex flex-col gap-3 group"
                    >
                      {/* Icon */}
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                        style={{ background: "rgba(59,130,246,0.18)", border: "1px solid rgba(59,130,246,0.25)" }}
                      >
                        <feature.icon className="w-5 h-5 text-blue-300 group-hover:text-blue-200 transition-colors duration-300" />
                      </div>
                      {/* Title */}
                      <h3 className="font-heading font-bold text-sm text-white leading-snug">
                        {feature.title}
                      </h3>
                      {/* Description */}
                      <p className="font-body text-xs text-white/55 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="mt-8">
                  <Link href="/about">
                    <Button
                      className="rounded-full px-6 py-3 font-button text-sm text-white border border-white/20 bg-white/10 hover:bg-white hover:text-[#0f2347] hover:border-white transition-all duration-200 hover:shadow-[0_0_20px_6px_rgba(255,255,255,0.25)]"
                      variant="ghost"
                    >
                      {t("features.cta")}
                    </Button>
                  </Link>
                </div>

              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Features;
